import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  LogOut, Search, Download, Archive, RefreshCw, Loader2, ExternalLink, LayoutGrid,
  Users2, CalendarDays, Settings2, MapPin, DollarSign, Phone, Star, Repeat, Save, Inbox,
  Images, UploadCloud, Trash2, Palette, Plus,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Logo } from "@/components/Logo";
import { LeadDialog } from "@/components/admin/LeadDialog";
import { GalleryManager } from "@/components/admin/GalleryManager";
import { BookingCalendar } from "@/components/admin/BookingCalendar";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { api } from "@/lib/api";
import { STATUS_META, STATUS_ORDER, formatDate } from "@/lib/adminMeta";

const inputCls =
  "w-full h-11 rounded-xl border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brandgreen focus:border-transparent";

function StatCard({ label, value, active, onClick, dot }) {
  return (
    <button
      onClick={onClick}
      className={`text-left rounded-2xl border p-4 transition-all ${active ? "border-brandgreen bg-brandgreen/5 shadow-sm" : "border-gray-100 bg-white hover:border-gray-200"}`}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-center gap-2">
        {dot && <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />}
        <span className="text-xs font-medium text-gray-500">{label}</span>
      </div>
      <span className="mt-1.5 block font-heading text-2xl font-bold text-navy">{value}</span>
    </button>
  );
}

function LeadCard({ lead, onClick }) {
  const meta = STATUS_META[lead.status] || STATUS_META.new;
  return (
    <button
      onClick={onClick}
      className="text-left rounded-2xl border border-gray-100 bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(15,61,94,0.08)]"
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-heading font-semibold text-navy truncate">{lead.full_name}</h3>
          <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
            <Phone className="w-3.5 h-3.5" /> {lead.phone}
          </p>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${meta.color}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} /> {meta.label}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {(lead.services || []).slice(0, 3).map((s) => (
          <span key={s} className="rounded-full bg-neutralbg px-2.5 py-1 text-[11px] font-medium text-navy">{s}</span>
        ))}
        {(lead.services || []).length > 3 && <span className="text-[11px] text-gray-400 px-1 py-1">+{lead.services.length - 3}</span>}
      </div>
      <div className="mt-4 flex items-center justify-between text-xs text-gray-400">
        <span className="flex items-center gap-1.5">{lead.suburb ? <><MapPin className="w-3.5 h-3.5" /> {lead.suburb}</> : "—"}</span>
        <span className="flex items-center gap-3">
          {lead.quote_amount != null && <span className="font-semibold text-brandgreen-dark flex items-center"><DollarSign className="w-3 h-3" />{lead.quote_amount}</span>}
          {formatDate(lead.created_at)}
        </span>
      </div>
    </button>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { logoUrl, refresh: refreshBranding } = useBranding();
  const logoRef = useRef(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [archived, setArchived] = useState(false);
  const [activeLead, setActiveLead] = useState(null);
  const [tab, setTab] = useState("leads");
  const [customers, setCustomers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [config, setConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [teams, setTeams] = useState([]);
  const [newTeam, setNewTeam] = useState({ name: "", color: "#3BB273" });
  const [refreshingReviews, setRefreshingReviews] = useState(false);

  const loadTeams = useCallback(async () => {
    try { const r = await api.get("/admin/teams"); setTeams(r.data); } catch { /* */ }
  }, []);

  const loadStats = useCallback(async () => {
    try { const r = await api.get("/admin/stats"); setStats(r.data); } catch { /* */ }
  }, []);

  const loadLeads = useCallback(async () => {
    setLoadingLeads(true);
    try {
      const params = { sort, archived };
      if (statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const r = await api.get("/admin/leads", { params });
      setLeads(r.data);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Session expired. Please sign in again."); }
      else toast.error("Could not load leads.");
    } finally { setLoadingLeads(false); }
  }, [sort, archived, statusFilter, search]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadTeams(); }, [loadTeams]);
  useEffect(() => {
    const t = setTimeout(loadLeads, 250);
    return () => clearTimeout(t);
  }, [loadLeads]);

  useEffect(() => {
    if (tab === "customers") api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    if (tab === "bookings") api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    if (tab === "settings") api.get("/site-config").then((r) => setConfig(r.data)).catch(() => setConfig({}));
  }, [tab]);

  const refresh = () => { loadStats(); loadLeads(); };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/leads-export/csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "jk-leads.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try { await api.put("/admin/site-config", config); toast.success("Settings saved"); }
    catch { toast.error("Could not save settings."); }
    finally { setSavingConfig(false); }
  };

  const onUpdated = () => { loadStats(); loadLeads(); if (tab === "bookings") api.get("/admin/bookings").then((r) => setBookings(r.data)); };

  const uploadLogo = async (file) => {
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      refreshBranding();
      toast.success("Logo updated");
    } catch { toast.error("Could not upload logo."); } finally { setLogoBusy(false); }
  };
  const removeLogo = async () => {
    try { await api.delete("/admin/branding/logo"); refreshBranding(); toast.success("Logo removed"); }
    catch { toast.error("Could not remove logo."); }
  };

  const addTeam = async () => {
    if (!newTeam.name.trim()) { toast.error("Enter a team name."); return; }
    try {
      await api.post("/admin/teams", { name: newTeam.name.trim(), color: newTeam.color });
      setNewTeam({ name: "", color: "#3BB273" });
      loadTeams();
      toast.success("Team added");
    } catch { toast.error("Could not add team."); }
  };
  const updateTeamColor = async (t, color) => {
    try { await api.patch(`/admin/teams/${t.id}`, { name: t.name, color }); loadTeams(); }
    catch { toast.error("Could not update team."); }
  };
  const deleteTeam = async (id) => {
    try { await api.delete(`/admin/teams/${id}`); loadTeams(); toast.success("Team removed"); }
    catch { toast.error("Could not remove team."); }
  };

  const refreshReviews = async () => {
    setRefreshingReviews(true);
    try {
      const r = await api.post("/admin/reviews/refresh");
      if (r.data?.source === "google") toast.success(`Synced ${r.data?.reviews?.length || 0} Google reviews (${r.data?.rating}★).`);
      else toast.warning("Refreshed — still using manual reviews. Check your Place ID & API key are correct and saved.");
    } catch { toast.error("Could not refresh reviews."); }
    finally { setRefreshingReviews(false); }
  };

  const reschedule = async (leadId, newDate) => {
    try {
      const r = await api.patch(`/admin/leads/${leadId}`, { booking_date: newDate });
      toast.success("Booking rescheduled");
      if (r.data?.booking_conflict) (toast.warning || toast)("Possible double-booking at this date/time.");
      api.get("/admin/bookings").then((res) => setBookings(res.data)).catch(() => {});
      loadStats();
    } catch { toast.error("Could not reschedule booking."); }
  };

  return (
    <div className="min-h-screen bg-neutralbg" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="hidden sm:inline text-xs font-semibold uppercase tracking-wider text-gray-400 border-l border-gray-200 pl-4">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-navy transition-colors"><ExternalLink className="w-4 h-4" /> View site</a>
            {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />}
            <span className="hidden md:inline text-sm font-medium text-navy max-w-[140px] truncate">{user?.name || user?.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-navy hover:bg-neutralbg transition-colors" data-testid="logout-btn">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 sm:px-8 py-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-navy">Dashboard</h1>
            <p className="text-sm text-gray-500">Manage your quote requests, leads and bookings.</p>
          </div>
          <button onClick={refresh} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-navy hover:bg-neutralbg transition-colors" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          <StatCard label="Total" value={stats.total} active={statusFilter === "all" && !archived} onClick={() => { setArchived(false); setStatusFilter("all"); }} />
          {STATUS_ORDER.map((s) => (
            <StatCard key={s} label={STATUS_META[s].label} value={stats.by_status?.[s] ?? 0} dot={STATUS_META[s].dot} active={statusFilter === s && !archived} onClick={() => { setArchived(false); setStatusFilter(s); }} />
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList className="bg-white border border-gray-100 rounded-full p-1 h-auto flex-wrap">
            <TabsTrigger value="leads" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white px-4 py-2" data-testid="tab-leads"><LayoutGrid className="w-4 h-4 mr-1.5" /> Leads</TabsTrigger>
            <TabsTrigger value="customers" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white px-4 py-2" data-testid="tab-customers"><Users2 className="w-4 h-4 mr-1.5" /> Customers</TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white px-4 py-2" data-testid="tab-bookings"><CalendarDays className="w-4 h-4 mr-1.5" /> Bookings</TabsTrigger>
            <TabsTrigger value="gallery" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white px-4 py-2" data-testid="tab-gallery"><Images className="w-4 h-4 mr-1.5" /> Gallery</TabsTrigger>
            <TabsTrigger value="settings" className="rounded-full data-[state=active]:bg-navy data-[state=active]:text-white px-4 py-2" data-testid="tab-settings"><Settings2 className="w-4 h-4 mr-1.5" /> Settings</TabsTrigger>
          </TabsList>

          {/* LEADS */}
          <TabsContent value="leads" className="mt-6">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone or suburb…" className={`${inputCls} pl-10`} data-testid="lead-search" />
              </div>
              <div className="flex gap-3">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="h-11 w-40" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="h-11 w-36" data-testid="sort-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                  </SelectContent>
                </Select>
                <button onClick={() => setArchived((a) => !a)} className={`inline-flex items-center gap-1.5 rounded-xl border px-4 h-11 text-sm font-semibold transition-colors ${archived ? "bg-navy text-white border-navy" : "border-gray-200 text-navy hover:bg-neutralbg"}`} data-testid="archived-toggle">
                  <Archive className="w-4 h-4" /> {archived ? "Archived" : "Archive"}
                </button>
                <button onClick={exportCsv} className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-4 h-11 text-sm font-semibold text-navy hover:bg-neutralbg transition-colors" data-testid="export-csv"><Download className="w-4 h-4" /> CSV</button>
              </div>
            </div>

            {loadingLeads ? (
              <div className="h-40 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-navy" /></div>
            ) : leads.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-14 text-center">
                <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-neutralbg mx-auto"><Inbox className="w-7 h-7 text-gray-300" /></span>
                <p className="mt-4 font-heading font-semibold text-navy">{archived ? "No archived leads" : "No leads yet"}</p>
                <p className="text-sm text-gray-500 mt-1">{archived ? "Archived leads will appear here." : "New quote requests will appear here automatically."}</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {leads.map((l) => <LeadCard key={l.id} lead={l} onClick={() => setActiveLead(l.id)} />)}
              </div>
            )}
          </TabsContent>

          {/* CUSTOMERS */}
          <TabsContent value="customers" className="mt-6">
            <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutralbg text-gray-500">
                    <tr>
                      <th className="text-left font-semibold px-5 py-3">Customer</th>
                      <th className="text-left font-semibold px-5 py-3">Suburb</th>
                      <th className="text-left font-semibold px-5 py-3">Leads</th>
                      <th className="text-left font-semibold px-5 py-3">Bookings</th>
                      <th className="text-left font-semibold px-5 py-3">Lifetime Value</th>
                      <th className="text-left font-semibold px-5 py-3">Last Activity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {customers.length === 0 ? (
                      <tr><td colSpan={6} className="px-5 py-10 text-center text-gray-400">No customers yet.</td></tr>
                    ) : customers.map((c) => (
                      <tr key={c.key} className="hover:bg-neutralbg/50">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-navy">{c.name}</span>
                            {c.is_repeat && <span className="inline-flex items-center gap-1 rounded-full bg-brandgreen/10 px-2 py-0.5 text-[11px] font-semibold text-brandgreen-dark"><Repeat className="w-3 h-3" /> Repeat</span>}
                          </div>
                          <div className="text-xs text-gray-400">{c.email}</div>
                        </td>
                        <td className="px-5 py-3 text-gray-600">{c.suburb || "—"}</td>
                        <td className="px-5 py-3 text-gray-600">{c.total_leads}</td>
                        <td className="px-5 py-3 text-gray-600">{c.total_bookings}</td>
                        <td className="px-5 py-3 font-semibold text-navy">${(c.lifetime_value || 0).toFixed(2)}</td>
                        <td className="px-5 py-3 text-gray-500">{formatDate(c.last_activity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* BOOKINGS */}
          <TabsContent value="bookings" className="mt-6">
            <BookingCalendar bookings={bookings} teams={teams} onSelect={setActiveLead} onReschedule={reschedule} />
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery" className="mt-6">
            <GalleryManager />
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings" className="mt-6 space-y-6">
            <div className="max-w-2xl rounded-2xl border border-gray-100 bg-white p-6">
              <h2 className="font-heading text-lg font-bold text-navy flex items-center gap-2"><Images className="w-5 h-5 text-brandgreen" /> Business logo</h2>
              <p className="text-sm text-gray-500 mt-1">Upload your logo to replace the placeholder across the site. A PNG with a transparent background works best.</p>
              <div className="mt-5 flex flex-wrap items-center gap-5">
                <div className="grid place-items-center w-44 h-24 rounded-xl border border-gray-100 bg-neutralbg/60 overflow-hidden p-2">
                  {logoUrl ? <img src={logoUrl} alt="Current logo" className="max-h-20 max-w-[160px] object-contain" data-testid="settings-logo-preview" /> : <span className="text-xs text-gray-400">No logo set</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <button onClick={() => logoRef.current?.click()} disabled={logoBusy} className="inline-flex items-center gap-2 rounded-full bg-brandgreen px-5 py-2.5 text-sm font-semibold text-white hover:bg-brandgreen-dark transition-colors disabled:opacity-50" data-testid="logo-upload-btn">
                    {logoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Upload logo
                  </button>
                  {logoUrl && <button onClick={removeLogo} className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-5 py-2.5 text-sm font-semibold text-red-500 hover:bg-red-50 transition-colors" data-testid="logo-remove-btn"><Trash2 className="w-4 h-4" /> Remove</button>}
                  <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadLogo(e.target.files[0])} data-testid="logo-file-input" />
                </div>
              </div>
            </div>
            <div className="max-w-2xl rounded-2xl border border-gray-100 bg-white p-6">
              <h2 className="font-heading text-lg font-bold text-navy">Integrations &amp; tracking</h2>
              <p className="text-sm text-gray-500 mt-1">Add your tracking IDs and Google Business Profile link. These load automatically on the public site.</p>
              {!config ? (
                <div className="h-32 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>
              ) : (
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  {[
                    ["ga4_id", "Google Analytics 4 ID", "G-XXXXXXX"],
                    ["gtm_id", "Google Tag Manager ID", "GTM-XXXXXX"],
                    ["google_ads_id", "Google Ads Conversion ID", "AW-XXXXXXX"],
                    ["meta_pixel_id", "Meta Pixel ID", "XXXXXXXXXX"],
                    ["clarity_id", "Microsoft Clarity ID", "xxxxxxxxxx"],
                    ["google_place_id", "Google Place ID (for live reviews)", "ChIJ..."],
                    ["google_places_api_key", "Google Places API Key (for live reviews)", "AIza..."],
                    ["google_business_url", "Google Business Profile URL", "https://…"],
                  ].map(([key, label, ph]) => (
                    <div key={key} className={["google_business_url", "google_places_api_key"].includes(key) ? "sm:col-span-2" : ""}>
                      <label className="text-sm font-medium text-navy mb-1.5 block">{label}</label>
                      <input className={inputCls} value={config[key] || ""} onChange={(e) => setConfig((c) => ({ ...c, [key]: e.target.value }))} placeholder={ph} data-testid={`config-${key}`} />
                    </div>
                  ))}
                </div>
              )}
              <button onClick={saveConfig} disabled={savingConfig || !config} className="mt-6 inline-flex items-center gap-2 rounded-full bg-brandgreen px-6 py-3 font-semibold text-white hover:bg-brandgreen-dark transition-colors active:scale-95" data-testid="save-config-btn">
                {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save settings
              </button>

              <div className="mt-8 pt-6 border-t border-gray-100">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                  <div className="flex items-start gap-2 text-sm text-gray-500">
                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400 shrink-0 mt-0.5" />
                    <span>Add your <strong>Google Place ID</strong> and <strong>Places API key</strong> above, save, then sync your live reviews. They cache for 6 hours.</span>
                  </div>
                  <button onClick={refreshReviews} disabled={refreshingReviews} className="shrink-0 inline-flex items-center gap-2 rounded-full border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy hover:bg-neutralbg transition-colors disabled:opacity-50" data-testid="refresh-reviews-btn">
                    {refreshingReviews ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh reviews now
                  </button>
                </div>
              </div>
            </div>

            {/* CLEANING TEAMS */}
            <div className="max-w-2xl rounded-2xl border border-gray-100 bg-white p-6" data-testid="teams-manager">
              <h2 className="font-heading text-lg font-bold text-navy flex items-center gap-2"><Palette className="w-5 h-5 text-brandgreen" /> Cleaning teams</h2>
              <p className="text-sm text-gray-500 mt-1">Create colour-coded teams to assign to jobs. Colours appear on the booking calendar.</p>

              <div className="mt-5 space-y-2">
                {teams.length === 0 ? (
                  <p className="text-sm text-gray-400 py-2">No teams yet. Add your first team below.</p>
                ) : teams.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 rounded-xl border border-gray-100 px-3 py-2.5" data-testid={`team-row-${t.id}`}>
                    <input type="color" value={t.color} onChange={(e) => updateTeamColor(t, e.target.value)} className="w-8 h-8 rounded-lg border border-gray-200 cursor-pointer bg-white p-0.5" data-testid={`team-color-${t.id}`} title="Change colour" />
                    <span className="flex-1 font-medium text-navy">{t.name}</span>
                    <button onClick={() => deleteTeam(t.id)} className="grid place-items-center w-9 h-9 rounded-lg border border-gray-200 text-red-500 hover:bg-red-50 transition-colors" data-testid={`team-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-center gap-3">
                <input type="color" value={newTeam.color} onChange={(e) => setNewTeam((s) => ({ ...s, color: e.target.value }))} className="w-11 h-11 rounded-lg border border-gray-200 cursor-pointer bg-white p-0.5 shrink-0" data-testid="new-team-color" title="Team colour" />
                <input value={newTeam.name} onChange={(e) => setNewTeam((s) => ({ ...s, name: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && addTeam()} placeholder="Team name (e.g. Team A)" className={inputCls} data-testid="new-team-name" />
                <button onClick={addTeam} className="shrink-0 inline-flex items-center gap-2 rounded-full bg-brandgreen px-5 h-11 font-semibold text-white hover:bg-brandgreen-dark transition-colors active:scale-95" data-testid="add-team-btn"><Plus className="w-4 h-4" /> Add</button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <LeadDialog leadId={activeLead} open={!!activeLead} onClose={() => setActiveLead(null)} onUpdated={onUpdated} teams={teams} />
    </div>
  );
}
